import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { authService } from '@/services/authService';
import { resetFailureKind } from '@/lib/authFailures';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { AuthIntro } from '@/components/auth/AuthIntro';
import { AuthCard } from '@/components/auth/AuthCard';
import { AuthField } from '@/components/auth/AuthField';
import { AuthAlert } from '@/components/auth/AuthAlert';
import { AuthLink } from '@/components/auth/AuthLink';
import { AuthButtonLink } from '@/components/auth/AuthButtonLink';
import { AuthSubmitButton } from '@/components/auth/AuthSubmitButton';

type ResetPasswordFormData = {
  password: string;
  confirmPassword: string;
};

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  /**
   * AL-1804: the address comes from the link, not from the user.
   *
   * The screen used to ask for the address of someone who had just received the
   * message that brought them here — and the link `CustomResetPassword` builds
   * has carried `?token=…&email=…` all along. That spare field was the last
   * difference left between the design and the screen.
   *
   * With no `email` in the link an empty address goes out, exactly as already
   * happens with a missing token: the server's refusal is the right answer for
   * a broken link, and this screen does not try to guess it.
   */
  const email = searchParams.get('email') || '';
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const schema = yup.object({
    password: yup
      .string()
      .min(8, t('app.auth.password_min_8'))
      .required(t('app.auth.password_required')),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], t('app.auth.passwords_do_not_match'))
      .required(t('app.auth.confirm_password_required')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      setLoading(true);
      setError(null);

      await authService.resetPassword(token, email, data.password, data.confirmPassword);
      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', {
          state: { message: t('app.auth.reset_success_redirect') },
        });
      }, 3000);
    } catch (err: unknown) {
      // AL-1780: the server's own wording never reaches the screen. The password
      // rules it enforces do — the browser cannot check them — but "no such
      // user" and "bad token" collapse into one answer, because telling them
      // apart names the account behind the address.
      const failure = resetFailureKind(err);

      if (failure.kind === 'password') {
        setError(failure.messages.map(entry => `• ${entry}`).join('\n'));
      } else if (failure.kind === 'throttled') {
        setError(t('app.auth.too_many_attempts'));
      } else if (failure.kind === 'invalidLink') {
        setError(t('app.auth.reset_link_invalid'));
      } else {
        setError(t('app.auth.reset_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-4 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-auth-accent">
            <Check aria-hidden="true" className="h-6 w-6 text-auth-accent-foreground" />
          </span>
          <h1 className="font-dm-sans text-[28px] font-bold leading-[32px] text-auth-heading sm:text-[40px] sm:leading-[44px]">
            {t('app.auth.all_done_title')}
          </h1>
        </div>

        <AuthCard>
          <div className="flex flex-col gap-4">
            <p className="text-center text-sm text-auth-body">
              {t('app.auth.reset_done_message')}
            </p>

            <AuthButtonLink to="/login">{t('app.auth.back_to_sign_in')}</AuthButtonLink>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {/* This screen has no subtitle, so the layout runs the headline at the
          card's width and lets it wrap, rather than on one long line. */}
      <div className="mx-auto w-full max-w-[380px]">
        <AuthIntro
          badge={t('app.auth.reset_badge')}
          title={t('app.auth.create_password_title')}
        />
      </div>

      <AuthCard>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          {error ? <AuthAlert>{error}</AuthAlert> : null}

          <AuthField
            {...register('password')}
            label={t('app.auth.new_password_label')}
            revealable
            autoComplete="new-password"
            placeholder={t('app.auth.new_password_placeholder')}
            error={errors.password?.message}
          />

          <AuthField
            {...register('confirmPassword')}
            label={t('app.auth.confirm_password_new_label')}
            revealable
            autoComplete="new-password"
            placeholder={t('app.auth.confirm_password_placeholder')}
            error={errors.confirmPassword?.message}
          />

          <AuthSubmitButton loading={loading} loadingLabel={t('app.auth.resetting')}>
            {t('app.auth.save_password')}
          </AuthSubmitButton>

          <p className="text-center text-sm">
            <AuthLink to="/login">← {t('app.auth.back_to_sign_in')}</AuthLink>
          </p>
        </form>
      </AuthCard>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
